import { Link } from "react-router-dom";
import { Navbar } from "@/components/landing/Navbar";
import { 
  BarChart3, 
  Wallet, 
  RefreshCw, 
  TrendingUp, 
  Shield, 
  Zap, 
  Eye, 
  Download,
  Settings,
  Target,
  Activity,
  DollarSign,
  PieChart,
  LineChart,
  AlertTriangle
} from "lucide-react";

export const Docs = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <div className="container px-4 pt-32 pb-16 max-w-6xl">
        <div className="prose prose-invert max-w-none">
          <h1 className="text-4xl font-bold mb-8">Sharpexio Documentation</h1>
          <p className="text-xl text-muted-foreground mb-12">
            Complete guide to using Sharpexio for cryptocurrency portfolio analytics and insights.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <div className="stat-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Wallet className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold">Getting Started</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Connect your wallet and sync your trading data in minutes.
              </p>
            </div>
            <div className="stat-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold">Analytics</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Deep insights into your trading performance and patterns.
              </p>
            </div>
            <div className="stat-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Target className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold">Optimization</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Use data-driven insights to improve your trading strategy.
              </p>
            </div>
          </div>

          <div className="space-y-16">
            {/* Getting Started Section */}
            <section>
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <Wallet className="w-8 h-8 text-primary" />
                Getting Started
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="bg-muted/50 rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4">Step 1: Connect Your Wallet</h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center text-xs font-bold text-primary mt-0.5">1</div>
                        <div>
                          <strong>Visit the Dashboard</strong>
                          <p className="text-sm">Click "Connect Wallet" on the main page</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center text-xs font-bold text-primary mt-0.5">2</div>
                        <div>
                          <strong>Choose Your Wallet</strong>
                          <p className="text-sm">Select MetaMask, WalletConnect, or other supported wallets</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center text-xs font-bold text-primary mt-0.5">3</div>
                        <div>
                          <strong>Authorize Connection</strong>
                          <p className="text-sm">Grant read-only access to your Hyperliquid account</p>
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-muted/50 rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4">Step 2: Sync Your Data</h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li className="flex items-start gap-3">
                        <RefreshCw className="w-5 h-5 text-primary mt-0.5" />
                        <div>
                          <strong>Automatic Sync</strong>
                          <p className="text-sm">Your trading history syncs automatically upon connection</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Activity className="w-5 h-5 text-primary mt-0.5" />
                        <div>
                          <strong>Real-time Updates</strong>
                          <p className="text-sm">Data refreshes periodically to show latest trades</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Zap className="w-5 h-5 text-primary mt-0.5" />
                        <div>
                          <strong>Manual Refresh</strong>
                          <p className="text-sm">Click refresh button to update data immediately</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-muted/50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold mb-4">🔒 Security First</h3>
                  <div className="space-y-4 text-muted-foreground">
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-green-500 mt-0.5" />
                      <div>
                        <strong>Read-Only Access</strong>
                        <p className="text-sm">We never request private keys or trading permissions</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Eye className="w-5 h-5 text-green-500 mt-0.5" />
                      <div>
                        <strong>Transparent Analytics</strong>
                        <p className="text-sm">All calculations are visible and verifiable</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
                      <div>
                        <strong>Risk Disclaimer</strong>
                        <p className="text-sm">Analytics are for insights only, not financial advice</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Dashboard Features */}
            <section>
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-primary" />
                Dashboard Features
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-muted/50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <TrendingUp className="w-6 h-6 text-primary" />
                    <h3 className="text-lg font-semibold">Performance Metrics</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Sharpe Ratio</li>
                    <li>• Total PnL</li>
                    <li>• Win Rate</li>
                    <li>• Average Trade Size</li>
                    <li>• Profit Factor</li>
                  </ul>
                </div>

                <div className="bg-muted/50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <DollarSign className="w-6 h-6 text-primary" />
                    <h3 className="text-lg font-semibold">Risk Analytics</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Maximum Drawdown</li>
                    <li>• Current Drawdown</li>
                    <li>• Risk-Adjusted Returns</li>
                    <li>• Volatility Metrics</li>
                    <li>• Position Sizing Analysis</li>
                  </ul>
                </div>

                <div className="bg-muted/50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <LineChart className="w-6 h-6 text-primary" />
                    <h3 className="text-lg font-semibold">Charts & Visualizations</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Equity Curve</li>
                    <li>• PnL Over Time</li>
                    <li>• Volume Analysis</li>
                    <li>• Drawdown Chart</li>
                    <li>• Performance Heatmap</li>
                  </ul>
                </div>

                <div className="bg-muted/50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <PieChart className="w-6 h-6 text-primary" />
                    <h3 className="text-lg font-semibold">Portfolio Breakdown</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Asset Allocation</li>
                    <li>• Sector Distribution</li>
                    <li>• Long vs Short Exposure</li>
                    <li>• Leverage Usage</li>
                    <li>• Coin Attribution</li>
                  </ul>
                </div>

                <div className="bg-muted/50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Activity className="w-6 h-6 text-primary" />
                    <h3 className="text-lg font-semibold">Trading Activity</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Recent Trades</li>
                    <li>• Trade Frequency</li>
                    <li>• Average Holding Period</li>
                    <li>• Trading Volume</li>
                    <li>• Fee Analysis</li>
                  </ul>
                </div>

                <div className="bg-muted/50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Download className="w-6 h-6 text-primary" />
                    <h3 className="text-lg font-semibold">Export Tools</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• CSV Export</li>
                    <li>• Trade History Download</li>
                    <li>• Performance Reports</li>
                    <li>• Tax Documents</li>
                    <li>• Custom Date Ranges</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Perpetual Trading */}
            <section>
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <Target className="w-8 h-8 text-primary" />
                Perpetual Trading Analytics
              </h2>

              <div className="space-y-6">
                <div className="bg-muted/50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold mb-4">Performance Analysis</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold mb-3">Key Metrics</h4>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li>• Perpetual PnL Tracking</li>
                        <li>• Funding Rate Analysis</li>
                        <li>• Leverage Optimization</li>
                        <li>• Position Duration Analysis</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-3">Risk Management</h4>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li>• Liquidation Risk Assessment</li>
                        <li>• Margin Usage Monitoring</li>
                        <li>• Correlation Analysis</li>
                        <li>• Position Sizing Insights</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-muted/50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold mb-4">Advanced Features</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <TrendingUp className="w-6 h-6 text-primary" />
                      </div>
                      <h4 className="font-semibold mb-2">Strategy Analysis</h4>
                      <p className="text-sm text-muted-foreground">Identify your most profitable strategies and patterns</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <Target className="w-6 h-6 text-primary" />
                      </div>
                      <h4 className="font-semibold mb-2">Position Tracking</h4>
                      <p className="text-sm text-muted-foreground">Monitor open positions and real-time PnL</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <Zap className="w-6 h-6 text-primary" />
                      </div>
                      <h4 className="font-semibold mb-2">Funding Analytics</h4>
                      <p className="text-sm text-muted-foreground">Track funding payments and optimize timing</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Spot Trading */}
            <section>
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <PieChart className="w-8 h-8 text-primary" />
                Spot Trading Analytics
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-muted/50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold mb-4">Portfolio Management</h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span>Holdings PnL tracking</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span>Asset allocation analysis</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span>Cost basis tracking</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span>Realized vs unrealized gains</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-muted/50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold mb-4">Performance Insights</h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span>Trade execution analysis</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span>Entry/exit timing</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span>Portfolio rebalancing suggestions</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span>Tax optimization insights</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Tips and Best Practices */}
            <section>
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <Settings className="w-8 h-8 text-primary" />
                Tips & Best Practices
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-muted/50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-green-500">✅ Do's</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Review metrics regularly</li>
                    <li>• Track performance over time</li>
                    <li>• Use insights to adjust strategy</li>
                    <li>• Monitor risk metrics closely</li>
                    <li>• Export data for tax purposes</li>
                  </ul>
                </div>

                <div className="bg-muted/50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-red-500">❌ Don'ts</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Don't chase past performance</li>
                    <li>• Don't ignore risk metrics</li>
                    <li>• Don't over-leverage based on analytics</li>
                    <li>• Don't treat insights as financial advice</li>
                    <li>• Don't share your wallet credentials</li>
                  </ul>
                </div>

                <div className="bg-muted/50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-blue-500">💡 Pro Tips</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Set up regular data sync</li>
                    <li>• Use custom date ranges for analysis</li>
                    <li>• Compare perpetual vs spot performance</li>
                    <li>• Track funding payments carefully</li>
                    <li>• Review strategy attribution monthly</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section>
              <h2 className="text-3xl font-bold mb-6">Frequently Asked Questions</h2>
              
              <div className="space-y-6">
                <div className="bg-muted/50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-3">How often is data updated?</h3>
                  <p className="text-muted-foreground">
                    Data syncs automatically every few minutes, and you can manually refresh at any time using the refresh button in the dashboard.
                  </p>
                </div>

                <div className="bg-muted/50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-3">Can I connect multiple wallets?</h3>
                  <p className="text-muted-foreground">
                    Currently, Sharpexio supports one wallet connection per session. You can disconnect and reconnect with a different wallet at any time.
                  </p>
                </div>

                <div className="bg-muted/50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-3">Is my data secure?</h3>
                  <p className="text-muted-foreground">
                    Yes. We use read-only access and never store private keys. All data is encrypted and transmitted securely using industry-standard protocols.
                  </p>
                </div>

                <div className="bg-muted/50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-3">What exchanges are supported?</h3>
                  <p className="text-muted-foreground">
                    Currently, we support Hyperliquid for perpetual and spot trading. We're working on adding support for more exchanges soon.
                  </p>
                </div>
              </div>
            </section>

            {/* Support */}
            <section>
              <h2 className="text-3xl font-bold mb-6">Need Help?</h2>
              <div className="bg-muted/50 rounded-lg p-6">
                <p className="text-muted-foreground mb-4">
                  If you need assistance with Sharpexio or have questions about any features, we're here to help.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
                  <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
                  <a href="https://x.com/sharpexio" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Contact on X</a>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-border">
            <p className="text-sm text-muted-foreground text-center">
              <Link to="/" className="text-primary hover:underline">← Back to Home</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
